import { Routes } from '@angular/router';
import { Plan, UserPermission } from './interfaces';
import { FullAccessGuard } from './routing-guards/full-access.guard';
import { HomeGuard } from './routing-guards/home.guard';
import { InviteGuard } from './routing-guards/invite.guard';

function resolveHomeComponent() {
    const host = window.location.hostname;
    const parts = host.split('.');
    const hasSubdomain = parts.length > 2;
    const subdomain = hasSubdomain ? parts[0] : null;
    const reservedSubdomains = ['biz', 'fans', 'auth', 'www', 'dev', 'tst'];

    if (!hasSubdomain || reservedSubdomains.includes(subdomain!)) {
        // No subdomain (mydreamteam.ai) or a reserved one (dev/tst/www) → B2C
        if (subdomain === 'biz' || subdomain === 'fans') {
            return import('./pages/home-biz/home-biz.component').then(m => m.HomeBizComponent);
        }
        return import('./pages/home/home.component').then(m => m.HomeComponent);
    }

    // Non-reserved subdomain → community
    return import('./pages/home-community/home-community.component').then(m => m.HomeCommunityComponent);
}

export const routes: Routes = [
    // Public routes - no authentication required
    // Home route is subdomain-aware:
    //   biz.*              → HomeBizComponent
    //   fans.*             → HomeBizComponent (token-driven terminology)
    //   auth.*             → excluded (handled by auth mechanics)
    //   www.*, dev.*, tst.*, mydreamteam.ai → HomeComponent (B2C)
    //   [any other slug].* → HomeCommunityComponent
    { path: 'home', loadComponent: () => resolveHomeComponent(), canActivate: [HomeGuard] },
    { path: 'home/:fbclid', loadComponent: () => resolveHomeComponent(), canActivate: [HomeGuard] },
    { path: 'home/:fbclid/:utm_source/:utm_camp_id/:utm_adset_id/:utm_ad_id', loadComponent: () => resolveHomeComponent(), canActivate: [HomeGuard] },
    { path: 'home/:utm_source/:transaction_id', loadComponent: () => resolveHomeComponent(), canActivate: [HomeGuard] },
    { path: 'view/:hid', loadComponent: () => import(`./pages/view-chat/view-chat.component`).then(m => m.ViewChatComponent) },
    { path: 'view/:hid/:fbclid', loadComponent: () => import(`./pages/view-chat/view-chat.component`).then(m => m.ViewChatComponent) },
    { path: 'view/:hid/:fbclid/:utm_source/:utm_camp_id/:utm_adset_id/:utm_ad_id', loadComponent: () => import(`./pages/view-chat/view-chat.component`).then(m => m.ViewChatComponent) },
    { path: 'view/:hid/:utm_source/:transaction_id', loadComponent: () => import(`./pages/view-chat/view-chat.component`).then(m => m.ViewChatComponent) },
    // B2C entry funnel (gated by ENTRY_FUNNEL_ENABLED; each component redirects to /home off the B2C root origin).
    // start/claim handles auth itself: it must not bounce while AppComponent is still validating the returning authToken.
    { path: 'start/claim', loadComponent: () => import('./pages/entry/entry-claim/entry-claim.component').then(m => m.EntryClaimComponent) },
    { path: 'start', loadComponent: () => import('./pages/entry/start-wizard/start-wizard.component').then(m => m.StartWizardComponent), canActivate: [HomeGuard] },
    { path: 'for/:slug', loadComponent: () => import('./pages/entry/domain-landing/domain-landing.component').then(m => m.DomainLandingComponent) },
    { path: 'for/:slug/:fbclid', loadComponent: () => import('./pages/entry/domain-landing/domain-landing.component').then(m => m.DomainLandingComponent) },
    { path: 'for/:slug/:fbclid/:utm_source/:utm_camp_id/:utm_adset_id/:utm_ad_id', loadComponent: () => import('./pages/entry/domain-landing/domain-landing.component').then(m => m.DomainLandingComponent) },
    { path: 'try-chat/:tryid', loadComponent: () => import(`./pages/chat/chat.component`).then(m => m.ChatComponent) },
    
    // Invitation entry point - works on any subdomain, always redirects to auth/signup with invkey
    { path: 'join',
        canActivate: [InviteGuard],
        loadComponent: () => import(`./pages/signup-short/signup-short.component`).then(m => m.SignupShortComponent)
    },

    // Auth routes - guest access allowed
    { path: 'signup', loadComponent: () => import(`./pages/signup-short/signup-short.component`).then(m => m.SignupShortComponent),
        canActivate: [FullAccessGuard],
        data: {
            allowedSubdomains: ['auth'],
            requiresAuth: false
        }
    },
    { path: 'signup/:fbclid', loadComponent: () => import(`./pages/signup-short/signup-short.component`).then(m => m.SignupShortComponent),
        canActivate: [FullAccessGuard],
        data: {
            allowedSubdomains: ['auth'],
            requiresAuth: false
        }
    },
    { path: 'signup/:fbclid/:utm_source/:utm_camp_id/:utm_adset_id/:utm_ad_id', loadComponent: () => import(`./pages/signup-short/signup-short.component`).then(m => m.SignupShortComponent),
        canActivate: [FullAccessGuard],
        data: {
            allowedSubdomains: ['auth'],
            requiresAuth: false
        }
    },
    { path: 'signin', loadComponent: () => import(`./pages/signin/signin.component`).then(m => m.SigninComponent),
        canActivate: [FullAccessGuard],
        data: {
            allowedSubdomains: ['auth'],
            requiresAuth: false
        }
    },
    { path: 'resetpassword', loadComponent: () => import(`./pages/resetpassword/resetpassword.component`).then(m => m.ResetpasswordComponent),
        canActivate: [FullAccessGuard],
        data: {
            allowedSubdomains: ['auth'],
            requiresAuth: false
        }
    },

    // Protected routes - require authentication and specific plans
    /* NOT USED IN MDT V2
    { path: 'team', loadComponent: () => import(`./pages/team/team.component`).then(m => m.TeamComponent),
        canActivate: [FullAccessGuard],
        data: {
            plans: [ Plan.BASIC, Plan.PREMIUM, Plan.PREMIUM_PLUS ]
        }
    },
    */ 
    { path: 'ask', loadComponent: () => import(`./pages/chat/chat.component`).then(m => m.ChatComponent),
        canActivate: [FullAccessGuard],
        data: {
            plans: [ Plan.BASIC, Plan.PREMIUM, Plan.PREMIUM_PLUS, Plan.BUS_USER_PAYS_BASIC, Plan.BUS_USER_PAYS_PREMIUM, Plan.BUS_USER_PAYS_PREMIUM_PLUS, Plan.BUS_USER_COMPANY_PAYS ],
            requiresAuth: true
        }
    },
    { path: 'ask/:hid', loadComponent: () => import(`./pages/chat/chat.component`).then(m => m.ChatComponent),
        canActivate: [FullAccessGuard],
        data: {
            plans: [ Plan.BASIC, Plan.PREMIUM, Plan.PREMIUM_PLUS, Plan.BUS_USER_PAYS_BASIC, Plan.BUS_USER_PAYS_PREMIUM, Plan.BUS_USER_PAYS_PREMIUM_PLUS, Plan.BUS_USER_COMPANY_PAYS ],
            requiresAuth: true
        }
    },   
    { path: 'history', loadComponent: () => import(`./pages/history/history.component`).then(m => m.HistoryComponent),
        canActivate: [FullAccessGuard],
        data: {
            plans: [ Plan.BASIC, Plan.PREMIUM, Plan.PREMIUM_PLUS, Plan.BUS_USER_PAYS_BASIC, Plan.BUS_USER_PAYS_PREMIUM, Plan.BUS_USER_PAYS_PREMIUM_PLUS, Plan.BUS_USER_COMPANY_PAYS ],
            requiresAuth: true
        }
    }, 
    { path: 'settings', loadComponent: () => import(`./pages/settings/settings.component`).then(m => m.SettingsComponent),
        canActivate: [FullAccessGuard],
        data: {
            plans: [ Plan.BASIC, Plan.PREMIUM, Plan.PREMIUM_PLUS, Plan.BUS_USER_PAYS_BASIC, Plan.BUS_USER_PAYS_PREMIUM, Plan.BUS_USER_PAYS_PREMIUM_PLUS, Plan.BUS_USER_COMPANY_PAYS ],
            requiresAuth: true
        }
    },
    { path: 'teams',
        loadComponent: () => import('./pages/teams/teams.component').then(c => c.TeamsComponent),
        canActivate: [FullAccessGuard],
        data: {
          requiresAuth: false,
          disallowedSubdomains: ['biz', 'fans'],
          softBlockNonPayingCommunity: true,
        }
    },
    { path: 'email-preferences', 
        loadComponent: () => import('./pages/email-preferences/email-preferences.component').then(c => c.EmailPreferencesComponent),
        canActivate: [FullAccessGuard],
        data: {
            allowedSubdomains: ['*'],
            requiresAuth: true
        } 
    },

    // Business admin routes - with permission-based access
    { path: 'business-admin', loadComponent: () => import(`./pages/business-admin/business-admin.component`).then(m => m.BusinessAdminComponent),
        canActivate: [FullAccessGuard],
        data: {
            permissions: [ UserPermission.BUSINESS_ADMIN, UserPermission.BUSINESS_TECH, UserPermission.BUSINESS_BILLING, UserPermission.SYS_ADMIN ]
        },
        children: [
            {
                path: 'admin-admin',
                loadComponent: () =>
                    import(`./pages/business-admin/admin-pages/admin-admin/admin-admin.component`).then(
                        (m) => m.AdminAdminComponent
                    ),
                canActivate: [FullAccessGuard],
                data: {
                    permissions: [ UserPermission.SYS_ADMIN ],                    
                    requiresAuth: true
                },
            },
            {
                path: 'admin-business-details',
                loadComponent: () =>
                    import(`./pages/business-admin/admin-pages/business-details/business-details.component`).then(
                        (m) => m.BusinessDetailsComponent
                    ),
                canActivate: [FullAccessGuard],
                data: {
                    permissions: [ UserPermission.BUSINESS_ADMIN, UserPermission.SYS_ADMIN ],
                    allowedSubdomains: ['biz', 'fans'],
                    requiresAuth: true
                },
            },     
            {
                path: 'admin-contacts',
                loadComponent: () =>
                    import(`./pages/business-admin/admin-pages/business-contacts/business-contacts.component`).then(
                        (m) => m.BusinessContactsComponent
                    ),
                canActivate: [FullAccessGuard],
                data: {
                    permissions: [ UserPermission.BUSINESS_ADMIN, UserPermission.SYS_ADMIN ],
                    allowedSubdomains: ['biz', 'fans'],
                    requiresAuth: true
                },
            },       
            {
                path: 'admin-communities',
                loadComponent: () =>
                    import(`./pages/business-admin/admin-pages/business-communities/business-communities.component`).then(
                        (m) => m.BusinessCommunitiesComponent
                    ),
                canActivate: [FullAccessGuard],
                data: {
                    permissions: [ UserPermission.BUSINESS_ADMIN, UserPermission.BUSINESS_TECH, UserPermission.SYS_ADMIN ],
                    allowedSubdomains: ['biz', 'fans'],
                    requiresAuth: true
                },
            },
            {
                path: 'admin-teams',
                loadComponent: () =>
                    import(`./pages/business-admin/admin-pages/business-teams/business-teams.component`).then(
                        (m) => m.BusinessTeamsComponent
                    ),
                canActivate: [FullAccessGuard],
                data: {
                    permissions: [ UserPermission.BUSINESS_ADMIN, UserPermission.BUSINESS_TECH, UserPermission.SYS_ADMIN ],
                    allowedSubdomains: ['biz', 'fans'],
                    requiresAuth: true
                },
            },
            {
                path: 'admin-advisors',
                loadComponent: () =>
                    import(`./pages/business-admin/admin-pages/business-advisors/business-advisors.component`).then(
                        (m) => m.BusinessAdvisorsComponent
                    ),
                canActivate: [FullAccessGuard],
                data: {
                    permissions: [ UserPermission.BUSINESS_ADMIN, UserPermission.BUSINESS_TECH, UserPermission.SYS_ADMIN ],
                    allowedSubdomains: ['biz', 'fans'],
                    requiresAuth: true
                },
            },
            {
                path: 'admin-knowledge',
                loadComponent: () =>
                  import(`./pages/business-admin/admin-pages/business-docs/business-docs.component`).then(
                    (m) => m.BusinessDocsComponent
                  ),
                canActivate: [FullAccessGuard],
                data: {
                  permissions: [UserPermission.BUSINESS_ADMIN, UserPermission.BUSINESS_TECH, UserPermission.SYS_ADMIN],
                  plans: [Plan.BUSINESS_PREMIUM_EXTEND],
                  allowedSubdomains: ['biz', 'fans'],
                  requiresAuth: true,
                  planLabel: 'Business Premium Extended'
                },
              },
            {
                path: 'biz-onboarding',
                loadComponent: () =>
                    import(`./pages/business-admin/admin-pages/business-onboarding/business-onboarding.component`).then(
                        (m) => m.BusinessOnboardingComponent
                    ),
                canActivate: [FullAccessGuard],
                data: {
                    permissions: [ UserPermission.BUSINESS_ADMIN, UserPermission.SYS_ADMIN ],
                    allowedSubdomains: ['biz', 'fans'],
                    requiresAuth: true
                },
            },
            {
                path: 'admin-reports',
                loadComponent: () =>
                    import(`./pages/business-admin/admin-pages/business-reports/business-reports.component`).then(
                        (m) => m.BusinessReportsComponent
                    ),
                canActivate: [FullAccessGuard],
                data: {
                    permissions: [ UserPermission.BUSINESS_ADMIN, UserPermission.SYS_ADMIN ],
                    allowedSubdomains: ['biz', 'fans'],
                    requiresAuth: true
                },
            },
            {
                path: 'admin-billing',
                loadComponent: () =>
                    import(`./pages/business-admin/admin-pages/business-billing/business-billing.component`).then(
                        (m) => m.BusinessBillingComponent
                    ),
                canActivate: [FullAccessGuard],
                data: {
                    permissions: [ UserPermission.BUSINESS_ADMIN, UserPermission.BUSINESS_BILLING, UserPermission.SYS_ADMIN ],
                    allowedSubdomains: ['biz', 'fans'],
                    requiresAuth: true
                },
            },
        ],
    },   

    // Public informational routes
    { path: 'terms', loadComponent: () => import(`./pages/terms/terms.component`).then(m => m.TermsComponent) },
    { path: 'help', loadComponent: () => import('./pages/help/help.component').then(m => m.HelpComponent) },
    { path: 'help/:id', loadComponent: () => import('./pages/help-article/help-article.component').then(m => m.HelpArticleComponent) },
    { path: 'plans', loadComponent: () => import(`./pages/buy/buy.component`).then(m => m.BuyComponent), canActivate: [FullAccessGuard], data: { requiresUserPaysCommunity: true, allowedSubdomains: ['*'], requiresAuth: false } },
    { path: 'plans/:fbclid', loadComponent: () => import(`./pages/buy/buy.component`).then(m => m.BuyComponent), canActivate: [FullAccessGuard], data: { requiresUserPaysCommunity: true, allowedSubdomains: ['*'], requiresAuth: false } },
    { path: 'plans/:fbclid/:utm_source/:utm_camp_id/:utm_adset_id/:utm_ad_id', loadComponent: () => import(`./pages/buy/buy.component`).then(m => m.BuyComponent), canActivate: [FullAccessGuard], data: { requiresUserPaysCommunity: true, allowedSubdomains: ['*'], requiresAuth: false } },
    { path: 'contact', loadComponent: () => import(`./pages/contact/contact.component`).then(m => m.ContactComponent) },  
    { path: 'contact/:fbclid', loadComponent: () => import(`./pages/contact/contact.component`).then(m => m.ContactComponent) },  
    { path: 'contact/:fbclid/:utm_source/:utm_camp_id/:utm_adset_id/:utm_ad_id', loadComponent: () => import(`./pages/contact/contact.component`).then(m => m.ContactComponent) }, 
    { path: 'privacy', loadComponent: () => import(`./pages/privacy/privacy.component`).then(m => m.PrivacyComponent) },
    { path: 'dpa', loadComponent: () => import(`./pages/dpa/dpa.component`).then(m => m.DpaComponent) },
    { path: 'member-operator-agreement', loadComponent: () => import(`./pages/member-operator-agreement/member-operator-agreement.component`).then(m => m.MemberOperatorAgreementComponent) },

    // Payment completion routes
    { path: 'paypal-create-complete', loadComponent: () => import(`./pages/paypal-create-complete/paypal-create-complete.component`).then(m => m.PaypalCreateCompleteComponent) },  
    { path: 'paypal-create-cancel', loadComponent: () => import(`./pages/paypal-create-cancel/paypal-create-cancel.component`).then(m => m.PaypalCreateCancelComponent) }, 
    
    // Default redirects
    { path: '', redirectTo: 'home', pathMatch: 'full' },
    {
        path: '',
        redirectTo: ({ queryParams }) => {            
            const fbclid = queryParams['fbclid'];
            if (fbclid !== undefined) {
            return `/home/${fbclid}`;
            } else {            
            return `/home`;
            }
        }, pathMatch: 'full'
    },
    { path: '**', redirectTo: 'home' }
];